import { FormHelperText } from '@mui/material';
import { FC, ReactNode } from 'react';

/**
 * 取得に失敗したことだけを伝える。Select のように「選択肢を隠す」ができない場所で使う
 * （隠すと現在値まで見えなくなる）。文言を `VehicleAttributeOptionsState` と共有するために分けてある。
 */
export const VehicleAttributeOptionsError: FC<{ isError: boolean }> = ({ isError }) => {
  if (!isError) return null;

  return (
    <FormHelperText error>
      選択肢を取得できませんでした。選択済みの値だけ表示しています。
    </FormHelperText>
  );
};

type Props = {
  /** 選択肢をまだ取得中（`useVehicleAttributeOptions` の isLoading） */
  isLoading: boolean;
  /** 選択肢の取得に失敗した（同 isError） */
  isError: boolean;
  /** 選択肢そのものの描画 */
  children: ReactNode;
};

/**
 * 車両条件の選択肢の取得状態を出し分ける。#329
 *
 * 取得中は選択肢を描かない。空の選択肢を出すと「会社に選択肢が 1 件も無い」ように見え、
 * 利用者がそう信じて別の値を選んでしまう（#313・P3 の指摘）。
 *
 * Why not 失敗時も選択肢を隠す: 失敗しても現在値だけは選択肢に残るので（`withCurrentValues`）、
 * 隠すと既存データの指定が画面から消え、外すことも直すこともできなくなる（設計書 §5-4）。
 * 「取得できなかった」と伝えたうえで、選択済みの値は描く。
 *
 * Why not 画面ごとに文言を書く: 8 画面に同じ分岐が散ると、どれか 1 つだけ直して
 * 残りが「読み込み中...」のまま止まる事故になる。文言と分岐をここ 1 か所に置く。
 */
export const VehicleAttributeOptionsState: FC<Props> = ({ isLoading, isError, children }) => {
  if (isLoading) return <FormHelperText>読み込み中...</FormHelperText>;

  return (
    <>
      <VehicleAttributeOptionsError isError={isError} />
      {children}
    </>
  );
};
